import { useState } from 'react';
import { useMedplum } from '@medplum/react';
import {
  DiagnosticReport,
  Communication,
  Practitioner,
} from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { HL7 } from '@consts/systems.consts';
import {
  DiagnosticReportReviewAction,
  DiagnosticReportReviewStatus,
} from '../../lims-types';
import {
  addReviewStatusExtension,
  checkAllReviewersApproved,
  completeReviewerTask,
  findReviewerTask,
} from '../../utils/fhir-helpers';

// -----------------------------------------------------------------------------
interface UseCommentSubmissionProps {
  reportId?: string;
  diagnosticReport: DiagnosticReport | null;
  currentUser?: Practitioner;
  onCommentSubmitted?: () => void;
  onReviewStatusChange?: (status: DiagnosticReportReviewStatus) => void;
  onDiagnosticReportUpdate?: (report: DiagnosticReport) => void;
}

// -----------------------------------------------------------------------------
export const useCommentSubmission = ({
  reportId,
  diagnosticReport,
  currentUser,
  onCommentSubmitted,
  onReviewStatusChange,
  onDiagnosticReportUpdate,
}: UseCommentSubmissionProps) => {
  const medplum = useMedplum();
  const [submittingComment, setSubmittingComment] = useState(false);
  const [reviewStatus, setReviewStatus] =
    useState<DiagnosticReportReviewAction>('comment');
  const [reviewComment, setReviewComment] = useState('');

  // ---------------------------------------------------------------------------
  const submitComment = async () => {
    if (
      !reviewComment.trim() ||
      !reportId ||
      !diagnosticReport ||
      !currentUser?.id
    ) {
      return;
    }

    setSubmittingComment(true);
    try {
      const communication: Communication = {
        resourceType: 'Communication',
        status: 'completed',
        category: [
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.COMMUNICATION.CATEGORY.BASE,
                code:
                  reviewStatus === 'comment'
                    ? HL7.TERMINOLOGY.CS.COMMUNICATION.CATEGORY.CODES
                        .INSTRUCTION
                    : HL7.TERMINOLOGY.CS.COMMUNICATION.CATEGORY.CODES
                        .NOTIFICATION,
                display:
                  reviewStatus === 'comment' ? 'Instruction' : 'Notification',
              },
            ],
          },
        ],
        subject: diagnosticReport.subject as any,
        basedOn: [{ reference: `DiagnosticReport/${reportId}` }],
        sender: { reference: `Practitioner/${currentUser.id}` },
        sent: new Date().toISOString(),
        payload: [
          {
            contentString:
              reviewStatus === 'comment'
                ? reviewComment.trim()
                : `Review Decision: ${reviewStatus.toUpperCase()}. Comments: ${reviewComment.trim()}`,
          },
        ],
        note: [
          {
            text:
              reviewStatus === 'comment'
                ? 'Review comment'
                : `Review status: ${reviewStatus}`,
            time: new Date().toISOString(),
          },
        ],
      };

      await medplum.createResource(communication);

      // If this is a review (not just a comment), handle the task completion and approval workflow
      if (reviewStatus !== 'comment') {
        // Find the current reviewer's task
        const reviewerTask = await findReviewerTask(
          medplum,
          reportId,
          currentUser.id
        );

        if (reviewerTask) {
          // Complete the reviewer's task
          await completeReviewerTask(
            medplum,
            reviewerTask,
            reviewStatus,
            reviewComment.trim()
          );
        }

        // Check if all reviewers have approved
        const allReviewStatus = await checkAllReviewersApproved(
          medplum,
          reportId
        );

        // Update current user's review status
        onReviewStatusChange?.(reviewStatus);

        // Update the diagnostic report with review status extension
        const reportWithReviewStatus = addReviewStatusExtension(
          diagnosticReport,
          allReviewStatus
        );

        // If all reviewers have approved and this is an approval, finalize the report
        if (
          allReviewStatus.allApproved &&
          reviewStatus === 'approved' &&
          diagnosticReport.status === 'preliminary'
        ) {
          const finalReport = {
            ...reportWithReviewStatus,
            status: 'final' as const,
          };

          await medplum.updateResource(finalReport);
          onDiagnosticReportUpdate?.(finalReport);

          notifications.show({
            title: 'Report Finalized',
            message:
              'All reviewers have approved. The diagnostic report has been finalized.',
            color: 'green',
          });
        } else {
          // Just update the review status extension
          await medplum.updateResource(reportWithReviewStatus);
          onDiagnosticReportUpdate?.(reportWithReviewStatus);

          if (reviewStatus === 'approved') {
            notifications.show({
              title: 'Review Approved',
              message: `Your approval has been recorded. ${allReviewStatus.pendingTasks} reviewer(s) still pending.`,
              color: 'blue',
            });
          } else {
            notifications.show({
              title: 'Changes Requested',
              message: 'Your review feedback has been submitted.',
              color: 'orange',
            });
          }
        }
      } else {
        // Just a comment
        notifications.show({
          title: 'Success',
          message: 'Comment added successfully',
          color: 'green',
        });
      }

      setReviewComment('');
      setReviewStatus('comment');
      onCommentSubmitted?.();
    } catch (error: any) {
      notifications.show({
        title:
          reviewStatus === 'comment'
            ? 'Error Adding Comment'
            : 'Error Submitting Review',
        message:
          error.message ||
          `Failed to ${reviewStatus === 'comment' ? 'add comment' : 'submit review'}`,
        color: 'red',
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  // ---------------------------------------------------------------------------
  return {
    submittingComment,
    reviewStatus,
    setReviewStatus,
    reviewComment,
    setReviewComment,
    submitComment,
  };
};
